import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import type { ActiveSubscription } from '../data/accountTypes'
import { useGetStripeSubscriptionQuery } from '../services/projectDataApi'
import { mockFetchSubscription } from '../dev/mockBilling'
import { isDevMock } from '../dev/isDevMock'
import { isUnauthorizedError } from '../lib/unauthorizedError'

export type { ActiveSubscription } from '../data/accountTypes'

export interface UseSubscriptionResult {
  subscription: ActiveSubscription | null
  isLoading: boolean
  error: string | null
  workspaceId: string | null
  refetch: () => void
}

/**
 * useSubscription — reads the current user's workspace subscription from GET /api/stripe-subscription
 * (the BFF resolves the billing workspace server-side). Mirrors the dashboard's useStripeSubscription.
 *
 * Backed by the shared RTK Query endpoint rather than a per-component fetch: PlanSelection,
 * SettingsModal and SubscriptionGate both call this hook and can be mounted
 * together, and a bare useEffect fetch made every one of them issue its own duplicate request.
 *
 * In UI-only dev-mock mode there is no backend, so a placeholder active subscription is returned to
 * avoid blocking the preview behind the gate.
 */
export function useSubscription(): UseSubscriptionResult {
  const user = useSelector((s: RootState) => s.auth.user)
  const isAuthenticated = Boolean(user)
  const devMock = isDevMock()

  const query = useGetStripeSubscriptionQuery(undefined, { skip: devMock || !isAuthenticated })

  if (devMock) {
    const { subscription, workspaceId } = mockFetchSubscription()
    return { subscription, workspaceId, isLoading: false, error: null, refetch: () => {} }
  }

  if (!isAuthenticated) {
    return { subscription: null, workspaceId: null, isLoading: false, error: null, refetch: () => {} }
  }

  return {
    subscription: query.data?.subscription ?? null,
    workspaceId: query.data?.workspaceId ?? null,
    isLoading: query.isLoading,
    // A 401 (the session lapsed; apiFetchForQuery may already be redirecting to login) is treated as
    // "no subscription" rather than surfacing a noisy message.
    error: query.error && !isUnauthorizedError(query.error) ? 'Failed to load subscription' : null,
    refetch: () => { void query.refetch() },
  }
}
